import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { RotateCcw, Trash2, ArrowUpDown, Building2, Package, HardHat, Truck, ClipboardList, Users, FileSignature, Recycle, PenLine } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const TYPES = [
  { key: "transazioni", label: "Transazioni", icon: ArrowUpDown },
  { key: "cantieri", label: "Cantieri", icon: Building2 },
  { key: "rapportini", label: "Rapportini", icon: ClipboardList },
  { key: "documenti", label: "RDO / ODA / CEL", icon: FileSignature },
  { key: "fir", label: "Formulari Rifiuti (FIR)", icon: Recycle },
  { key: "fornitori", label: "Fornitori", icon: Package },
  { key: "operai", label: "Operai", icon: HardHat },
  { key: "mezzi", label: "Mezzi", icon: Truck },
  { key: "direttori", label: "Direttori di cantiere", icon: PenLine },
  { key: "utenti", label: "Utenti", icon: Users },
];

export default function Cestino() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [toPurge, setToPurge] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setData(await api.get("/trash"));
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const restore = async (tipo, id) => {
    try {
      await api.post("/trash/restore", { tipo, id });
      toast.success("Elemento ripristinato.");
      load();
    } catch (err) { toast.error(err.message); }
  };

  const confirmPurge = async () => {
    try {
      await api.del("/trash/purge", { tipo: toPurge.tipo, id: toPurge.id });
      toast.success("Eliminato definitivamente.");
      setToPurge(null); load();
    } catch (err) { toast.error(err.message); }
  };

  const totalItems = Object.values(data).reduce((s, arr) => s + (arr?.length || 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Cestino</h1>
        <p className="text-sm text-muted-foreground mt-1">Ripristina o elimina definitivamente gli elementi cancellati.</p>
      </div>

      {loading ? (
        <div className="h-40 rounded-xl bg-secondary animate-pulse" />
      ) : totalItems === 0 ? (
        <Card><CardContent className="py-16 text-center">
          <Trash2 className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-4 font-medium">Il cestino è vuoto</p>
          <p className="text-sm text-muted-foreground">Gli elementi eliminati appariranno qui.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {TYPES.filter((t) => data[t.key]?.length > 0).map((t) => (
            <Card key={t.key}>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><t.icon className="h-4 w-4" /> {t.label} <span className="text-xs text-muted-foreground">({data[t.key].length})</span></CardTitle></CardHeader>
              <CardContent className="divide-y divide-border">
                {data[t.key].map((row) => (
                  <div key={row.id} className="flex items-center justify-between py-3" data-testid={`trash-${t.key}-${row.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{row.label}</p>
                      <p className="text-xs text-muted-foreground">Eliminato il {fmtDate(row.deleted_at)}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button variant="outline" size="sm" className="gap-1.5" data-testid={`restore-${t.key}-${row.id}`} onClick={() => restore(t.key, row.id)}>
                        <RotateCcw className="h-3.5 w-3.5" /> Ripristina
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" data-testid={`purge-${t.key}-${row.id}`} onClick={() => setToPurge({ tipo: t.key, id: row.id, label: row.label })}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={Boolean(toPurge)} onOpenChange={(o) => !o && setToPurge(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare definitivamente?</AlertDialogTitle>
            <AlertDialogDescription>"{toPurge?.label}" verrà eliminato per sempre. Questa azione non può essere annullata.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmPurge} className="bg-destructive hover:bg-destructive/90">Elimina per sempre</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
